<template>
  <div class="h-100">
    <div class="search">
      <search :o="list"></search>
    </div>
    <div class="d-flex p-1 pb-2 header"
         style="height: 45px">
      <div class="d-flex actions my-auto mr-2">
        <div v-if="!getSelectedHouseholds().length">
          <span class="alt-action text-secondary">Select one or more households to create a group</span>
        </div>
        <div v-else
             title="Create group"
             class="btn btn-outline-secondary"
             v-on:click="createGroup()">
          <i class="fas fa-users mr-1"></i>
          <span>Create group</span>
        </div>
      </div>
      <div class="flex-grow-1 my-auto text-right">
        <span v-text="records()"></span>
      </div>
      <div v-if="ready" class="ml-2 dropdown">
        <button class="btn btn-link text-secondary" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          <i class="fas fa-ellipsis-v"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-right">
          <a class="dropdown-item" href="#" @click.prevent="downloadCSV">
            <i class="fas fa-download mr-2"></i>
            Download CSV
          </a>
        </div>
      </div>
    </div>
    <template v-if="ready">
      <scroll-area class="pr-3">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>
                <checkbox :o="table"
                          p="selected"
                          v-on:change="selectAll"></checkbox>
              </th>
              <th>
                <span>Household</span>
              </th>
              <th></th>
              <th>
                <span>Contract</span>
              </th>
              <th>
                <span>Date</span>
              </th>
              <th>
                <span>Accounts</span>
              </th>
              <th>
                <span>Value</span>
              </th>
              <th>
                <span>Schedules</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(household, i) in getHouseholds"
                :key="household._id">
              <td>
                <checkbox :o="household"
                          p="selected"
                          :disabled="getHouseholdGroup(household)"></checkbox>
              </td>
              <td>
                <span v-text="household.name"></span>
              </td>
              <td>
                <i v-show="getHouseholdGroup(household)"
                   class="fas fa-users"></i>
              </td>
              <td>
                <a href="#" class="badge badge-custom"
                      v-on:click="openContractModal(household)"
                      v-text="getContractId(household)"></a>
              </td>
              <td style="white-space: nowrap">
                <span v-text="getContractDate(household)"></span>
              </td>
              <td>
                <div style="display: flex; flex-direction: column; gap: 4px">
                  <span v-for="account in getHouseholdAccounts(household)"
                        :key="account._id"
                        v-text="account.number"></span>
                </div>
              </td>
              <td>
                <span v-text="showHouseholdValue(household)"></span>
              </td>
              <td>
                <schedule-badges :schedules="getSchedulesForHousehold(household)"></schedule-badges>
              </td>
            </tr>
          </tbody>
        </table>
      </scroll-area>
    </template>
    <template v-else>
      <loading></loading>
    </template>
  </div>
</template>

<style scoped>

.dropdown-item {
  color: #212529 !important; 
  font-size: 12px;
}

span.alt-action {
  font-weight: 200;
  font-style: italic;
  font-size: 0.8rem;
}

div.search {
  margin-right: 1em;
  margin-top: -3em;
  float: right;
  width: 15em;
}

div.header div.actions,
div.header div.actions div {
  margin-right: 1em;
  cursor: pointer;
  font-size: 0.8rem;
}

i {
  opacity: 0.3;
}

i:hover,
i.sort,
i.active {
  cursor: pointer;
  opacity: 1;
}

i.active {
  color: red;
}

span.link {
  cursor: pointer;
  text-decoration: underline;
}

span {
  font-size: 0.9em;
}

span.badge {
  font-weight: normal;
  font-size: 0.8em;
}
</style>

<script>
import { scrollArea, search, checkbox, loading } from "../../../../component";
import { Api, Csv, List, Modal, Record } from "../../../../factory";
import { numeral } from "../../../../npm";
import ScheduleBadges from "../../../../component/schedule/schedule-badges.vue";

export default {
  name: `FirmHouseholds`,
  get components() {
    return {
      scrollArea,
      search,
      checkbox,
      loading,
      ScheduleBadges,
    };
  },
  data() {
    return {
      table: {
        selected: ``,
      },
      list: {
        search: ``,
        sort: { asc: ``, key: `` },
      },
      ready: false,
      allHouseholds: [],
      allContracts: [],
      accountsValuesMap: new Map(),
      contractsByHouseholdId: new Map(),
      groupByHouseholdId: new Map(),
    };
  },
  computed: {
    getHouseholds: function () {
      const { search, sort } = this.list;
      return List.sort(
        List.filter(this.allHouseholds, search),
        sort.key,
        sort.asc
      );
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      // Parallelize independent API calls instead of sequential awaits
      await Promise.all([
        this.getAllHouseholds(),
        this.getAllContracts(),
        this.getAllGroups(),
      ]);

      // Sort here after both allHouseholds and allContracts are guaranteed populated
      this.allHouseholds.sort(
        (a, b) => this.getHouseholdValue(b) - this.getHouseholdValue(a)
      );

      this.ready = true;
    },
    createGroup() {
      return Modal.open(`group-create`, {
        households: this.getSelectedHouseholds(),
        methods: {
          success: () => {
            Modal.close();
            this.ready = false;
            this.init();
          },
        },
      });
    },
    selectAll() {
      const ids = this.getHouseholds.map((h) => h._id);
      this.allHouseholds.map((h) => {
        if (ids.includes(h._id) && !this.getHouseholdGroup(h)) {
          h.selected = this.table.selected;
        }
        return h;
      });
    },
    records() {
      return List.records(this.getHouseholds, this.allHouseholds);
    },
    async getAllHouseholds() {
      // Get basic household info first
      this.allHouseholds = (await Api.get(`households`)).data.map((h) => ({
        ...h,
        selected: ``,
        _loaded: false, // Track if detailed data is loaded
      }));
    },

    async loadHouseholdDetails(household) {
      if (household._loaded) return household;
      
      try {
        // Fetch full household details only when needed
        const { data } = await Api.get(`households/${household._id}`);
        
        // Update the household in our list with full details
        const index = this.allHouseholds.findIndex(h => h._id === household._id);
        if (index !== -1) {
          this.allHouseholds[index] = {
            ...data,
            selected: this.allHouseholds[index].selected,
            _loaded: true
          };
        }
        
        return data;
      } catch (error) {
        console.error(`Error loading household details:`, error);
        return household;
      }
    },
    async getAllContracts() {
      const contractValues = (await Api.get(`contracts/list-with-values`)).data;
      this.allContracts = contractValues.contracts;
      contractValues.accountsWithValues.forEach((acc) =>
        this.accountsValuesMap.set(acc.number, acc.value),
      );
      // Build O(1) lookup map: householdId -> active contract
      this.contractsByHouseholdId = new Map(
        this.allContracts
          .filter(c => c.status == 2)
          .map(c => [c.householdId, c])
      );
    },
    async getAllGroups() {
      this.allGroups = (await Api.get(`groups`)).data;
      // Build O(1) lookup map: householdId -> group
      this.groupByHouseholdId = new Map(
        this.allGroups.flatMap(g =>
          g.householdIds.map(id => [id, g])
        )
      );
    },
    getSelectedHouseholds() {
      return this.getHouseholds.filter((h) => h.selected);
    },
    getActiveContractFromHousehold(household) {
      return this.contractsByHouseholdId.get(household._id);
    },
    getScheduleForAccount(contract, account) {
      return contract.schedules.find(s => s._id == account.scheduleId);
    },
    getSchedulesInContracts() {
      const schedules = [];
      this.allContracts.forEach((c) => {
        c.accounts.forEach((acc) => {
          const schedule = c.schedules.find(s => s._id == acc.scheduleId);
          if (!schedules.some((s) => s.name == schedule.name))
            schedules.push(schedule);
        });
      });
      return schedules;
    },
    getContractId(household) {
      const contract = this.getActiveContractFromHousehold(household);
      return contract ? Record.id(contract) : "";
    },
    getSchedulesForHousehold(household) {
      const contract = this.getActiveContractFromHousehold(household);
      return contract ? contract.schedules : [];
    },
    getContractDate(household) {
      const contract = this.getActiveContractFromHousehold(household);
      return contract ? contract.date : "-----";
    },
    getContractRef(household) {
      const contract = this.getActiveContractFromHousehold(household);
      return contract ? contract.ref : "-----";
    },
    getHouseholdAccounts(household) {
      const contract = this.getActiveContractFromHousehold(household);
      return contract ? contract.accounts : [];
    },
    async openContractModal(household) {
      // Load full household details before opening modal
      const loadedHousehold = await this.loadHouseholdDetails(household);
      const contract = this.getActiveContractFromHousehold(loadedHousehold);
      
      if (contract)
        Modal.open(`contract`, {
          contract,
          methods: {
            success: () => {
              Modal.close();
              this.getAllContracts();
            },
          },
        });
    },
    getHouseholdValue(household) {
      const accounts = this.getHouseholdAccounts(household);

      return accounts.reduce(
        (total, acc) => this.accountsValuesMap.get(acc.number) + total,
        0
      );
    },
    showHouseholdValue(household) {
      const totalValue = this.getHouseholdValue(household);

      return numeral(totalValue).format(`$0,0.00`);
    },
    getHouseholdGroup(household) {
      return this.groupByHouseholdId.get(household._id);
    },
    downloadCSV() {
      const headers = [
        "Household",
        "Contract",
        "Date",
        "Accounts",
        "Value",
        "Schedules",
      ];
      const data = this.getHouseholds.map((household) => [
        household.name,
        this.getContractId(household),
        this.getContractDate(household),
        this.getHouseholdAccounts(household)
          .map((account) => account.number)
          .join(", "),
        this.showHouseholdValue(household),
        Object.values(this.getSchedulesForHousehold(household))
          .map((schedule) => schedule.name)
          .join(", "),
      ]);

      Csv.download("households.csv", headers, data);
    },
  },
};
</script>
